<!-- First message from session (before events arrive) -->
<ng-container *ngIf="turns.length === 0 && session?.firstMessage">
  <div class="user-message mb-8 ml-4 mr-8">
    <div class="flex justify-start">
      <div class="border-s200 bg-s100 max-w-[90%] rounded-2xl border px-5 py-3">
        <div class="text-s1 whitespace-pre-wrap break-words text-xl">{{
          session.firstMessage
        }}</div>
      </div>
    </div>
  </div>
  <div class="response-div">
    <div *ngIf="isSessionError" class="mb-4 ml-10 flex justify-start">
      <div class="max-w-[90%] rounded-2xl bg-red-50 px-4 py-3">
        <div class="break-words text-xl text-red-700">
          Session activation failed. Please try again.
        </div>
      </div>
    </div>
  </div>
</ng-container>

<!-- Turn-based message rendering -->
<ng-container
  *ngFor="
    let turn of turns;
    let isLast = last;
    let turnIndex = index;
    trackBy: trackByIndex
  "
>
  <!-- User message -->
  <div *ngIf="turn.userMessage" class="user-message mb-8 ml-4 mr-8">
    <div class="flex justify-start">
      <div class="border-s200 bg-s100 max-w-[90%] rounded-2xl border px-5 py-3">
        <div class="text-s1 whitespace-pre-wrap break-words text-xl">{{
          turn.userMessage.text
        }}</div>
      </div>
    </div>
    <!-- Meta info: agent / model / variant -->
    <div class="ml-4 mt-1 flex items-center justify-start gap-3">
      <span
        *ngIf="turn.userMessage.agentName || turn.userMessage.modelId"
        class="text-s500 text-base font-medium"
        >{{ getMetaText(turn.userMessage) }}</span
      >

      <button
        *ngIf="
          showPromptEnabled &&
          session?.type === sessionTypeEnum.Explorer &&
          turnIndex === 0 &&
          turn.userMessage.systemPrompt
        "
        type="button"
        class="text-m600 hover:text-m700 text-base font-medium focus:outline-none"
        (click)="
          openSystemPrompt({ systemPrompt: turn.userMessage.systemPrompt })
        "
        >Show Prompt</button
      >
    </div>
  </div>

  <!-- Response div wrapping all non-user content for this turn -->
  <div class="response-div mb-8 mr-8">
    <div *ngFor="let msg of turn.responses; trackBy: trackByIndex" class="mb-4">
      <!-- Agent message -->
      <div *ngIf="msg.role === 'agent'" class="ml-10">
        <div class="rounded-2xl px-4 py-3">
          <div
            class="prose prose-gray max-w-none break-words text-xl text-gray-800"
          >
            <remark [markdown]="msg.text">
              <ng-template [remarkTemplate]="'link'" let-node>
                <ng-container
                  *ngIf="parseMproveTabUrl(node.url) as tabId; else extLink"
                >
                  <button
                    type="button"
                    class="text-m600 ml-2 mt-1 inline-flex items-start gap-2 text-left align-middle leading-6"
                    [title]="node.title ?? ''"
                    (click)="onMproveTabLinkClick(tabId)"
                  >
                    <ng-container
                      *ngIf="
                        getMproveTabChartType({ tabId: tabId }) as chartType
                      "
                    >
                      <span
                        class="inline-flex h-6 w-6 flex-shrink-0 items-center justify-center align-middle [&_svg]:h-6 [&_svg]:w-6"
                      >
                        <m-chart-table-icon
                          *ngIf="chartType === chartTypeEnum.Table"
                        ></m-chart-table-icon>

                        <m-chart-line-icon
                          *ngIf="chartType === chartTypeEnum.Line"
                        ></m-chart-line-icon>

                        <m-chart-bar-icon
                          *ngIf="chartType === chartTypeEnum.Bar"
                        ></m-chart-bar-icon>

                        <m-chart-scatter-icon
                          *ngIf="chartType === chartTypeEnum.Scatter"
                        ></m-chart-scatter-icon>

                        <m-chart-single-icon
                          *ngIf="chartType === chartTypeEnum.Single"
                        ></m-chart-single-icon>

                        <m-chart-pie-icon
                          *ngIf="chartType === chartTypeEnum.Pie"
                        ></m-chart-pie-icon>

                        <m-chart-pivot-table-icon
                          *ngIf="chartType === chartTypeEnum.PivotTable"
                        ></m-chart-pivot-table-icon>
                      </span>
                    </ng-container>

                    <span [remarkNode]="node"></span>
                  </button>
                </ng-container>

                <ng-template #extLink>
                  <a
                    [href]="node.url"
                    target="_blank"
                    rel="noopener noreferrer"
                    [title]="node.title ?? ''"
                    [remarkNode]="node"
                  ></a>
                </ng-template>
              </ng-template>

              <ng-template [remarkTemplate]="'code'" let-node>
                <code-editor
                  class="border-s200 block max-h-96 overflow-auto rounded-lg border"
                  [ngModel]="node.value"
                  [readonly]="true"
                  [languages]="codeLanguages"
                  [language]="getCodeLanguage({ lang: node.lang })"
                  [theme]="codeTheme"
                  [setup]="null"
                ></code-editor>
              </ng-template>

              <ng-template [remarkTemplate]="'image'" let-node>
                <a
                  class="block"
                  [href]="node.url"
                  target="_blank"
                  rel="noopener noreferrer"
                  >[image: {{ node.alt || 'link' }}]</a
                >
              </ng-template>
            </remark>
          </div>
        </div>
      </div>

      <!-- Question/permission (inline, not clickable) -->
      <div
        *ngIf="
          msg.role === 'tool' &&
          msg.toolPart &&
          msg.toolPart.tool === 'question'
        "
        class="ml-[34px] flex items-start gap-3"
      >
        <span
          class="mt-4 h-2.5 w-2.5 flex-shrink-0 rounded-full"
          [ngClass]="{
            'bg-amber-400':
              msg.toolPart.state?.status === 'pending' ||
              msg.toolPart.state?.status === 'running',
            'bg-g500': msg.toolPart.state?.status === 'completed',
            'bg-red-500': msg.toolPart.state?.status === 'error'
          }"
        ></span>
        <div class="border-s200 max-w-[90%] overflow-hidden rounded-lg border">
          <div class="flex items-center gap-2 px-3 py-2">
            <span class="text-s1 text-lg font-medium">{{
              getQuestionStatus(msg.toolPart)
            }}</span>
          </div>
          <div
            *ngIf="getToolOutput(msg.toolPart)"
            class="border-s200 border-t px-3 py-2"
          >
            <div class="text-s1 whitespace-pre-wrap break-words text-xl">{{
              getToolOutput(msg.toolPart)
            }}</div>
          </div>
        </div>
      </div>

      <!-- Tool call: BASH with inline output -->
      <div
        *ngIf="
          msg.role === 'tool' && msg.toolPart && msg.toolPart.tool === 'bash'
        "
        class="ml-[34px] flex items-start gap-3"
      >
        <span
          class="mt-4 h-2.5 w-2.5 flex-shrink-0 rounded-full"
          [ngClass]="{
            'bg-amber-400':
              msg.toolPart.state?.status === 'pending' ||
              msg.toolPart.state?.status === 'running',
            'bg-g500': msg.toolPart.state?.status === 'completed',
            'bg-red-500': msg.toolPart.state?.status === 'error'
          }"
        ></span>
        <div class="border-s200 min-w-0 flex-1 rounded-lg border">
          <!-- Header -->
          <div class="flex w-full items-center gap-2 px-3 py-2">
            <span class="text-s1 text-lg font-medium">{{
              getToolTitle(msg.toolPart.tool)
            }}</span>
            <span
              *ngIf="getToolSubtitle(msg.toolPart)"
              class="text-s500 min-w-0 flex-1 truncate text-lg"
              >{{ getToolSubtitle(msg.toolPart) }}</span
            >
          </div>

          <!-- Inline output preview -->
          <div
            *ngIf="getToolOutput(msg.toolPart)"
            class="border-s200 min-h-[40px] cursor-pointer overflow-hidden rounded-b-lg border-t px-3 pt-2 hover:bg-gray-50"
            (click)="openToolOutput(msg.toolPart)"
          >
            <pre
              class="text-s1 whitespace-pre-wrap break-words font-mono text-base"
              >{{ getBashOutputPreview(msg.toolPart) }}</pre
            >
          </div>
        </div>
      </div>

      <!-- Tool call: NON-BASH (existing behavior) -->
      <div
        *ngIf="
          msg.role === 'tool' &&
          msg.toolPart &&
          msg.toolPart.tool !== 'question' &&
          msg.toolPart.tool !== 'bash'
        "
        class="ml-[34px] flex items-start gap-3"
      >
        <span
          class="mt-4 h-2.5 w-2.5 flex-shrink-0 rounded-full"
          [ngClass]="{
            'bg-amber-400':
              msg.toolPart.state?.status === 'pending' ||
              msg.toolPart.state?.status === 'running',
            'bg-g500': msg.toolPart.state?.status === 'completed',
            'bg-red-500': msg.toolPart.state?.status === 'error'
          }"
        ></span>
        <div
          class="border-s200 min-w-0 flex-1 overflow-hidden rounded-lg border"
        >
          <button
            class="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-gray-50 focus:outline-none disabled:hover:bg-transparent"
            [disabled]="!hasToolContent(msg.toolPart)"
            (click)="openToolOutput(msg.toolPart)"
          >
            <span class="text-s1 text-lg font-medium">{{
              getToolTitle(msg.toolPart.tool)
            }}</span>
            <span
              *ngIf="getToolSubtitle(msg.toolPart)"
              class="text-s500 min-w-0 flex-1 truncate text-lg"
              >{{ getToolSubtitle(msg.toolPart) }}</span
            >
          </button>
        </div>
      </div>

      <!-- Tool call (fallback without toolPart) -->
      <div
        *ngIf="msg.role === 'tool' && !msg.toolPart"
        class="ml-10 flex justify-start"
      >
        <div class="border-s200 max-w-[90%] rounded-lg border px-3 py-2">
          <div class="text-s600 break-words text-lg">{{ msg.text }}</div>
        </div>
      </div>

      <!-- Thought -->
      <div *ngIf="msg.role === 'thought'" class="ml-10 flex justify-start">
        <div class="max-w-[90%] rounded-2xl px-4 py-2">
          <div class="text-s500 whitespace-pre-wrap break-words text-xl">{{
            msg.text
          }}</div>
        </div>
      </div>

      <!-- Compaction indicator -->
      <div
        *ngIf="msg.role === 'compaction'"
        class="ml-[34px] flex items-start gap-3"
      >
        <span
          class="mt-4 h-2.5 w-2.5 flex-shrink-0 rounded-full bg-blue-400"
        ></span>
        <div class="border-s200 max-w-[90%] overflow-hidden rounded-lg border">
          <div class="flex items-center gap-2 px-3 py-2">
            <span class="text-s1 text-lg font-medium">{{ msg.text }}</span>
          </div>
        </div>
      </div>

      <!-- Interrupted indicator -->
      <div
        *ngIf="msg.role === 'interrupted'"
        class="ml-[34px] flex items-start gap-3"
      >
        <span
          class="mt-4 h-2.5 w-2.5 flex-shrink-0 rounded-full bg-amber-400"
        ></span>
        <div class="border-s200 max-w-[90%] overflow-hidden rounded-lg border">
          <div class="flex items-center gap-2 px-3 py-2">
            <span class="text-s500 text-lg font-medium">{{ msg.text }}</span>
          </div>
        </div>
      </div>

      <!-- Error -->
      <div *ngIf="msg.role === 'error'" class="ml-10 flex justify-start">
        <div class="max-w-[90%] rounded-2xl bg-red-50 px-4 py-3">
          <div class="break-words text-xl text-red-700">{{ msg.text }}</div>
        </div>
      </div>
    </div>

    <!-- File changes summary (only show when turn is complete) -->
    <div
      *ngIf="turn.fileDiffs?.length > 0 && (!isLast || !isSessionBusy)"
      class="mb-4 ml-[34px] flex items-start gap-3"
    >
      <span class="bg-g500 mt-4 h-2.5 w-2.5 flex-shrink-0 rounded-full"></span>
      <div class="border-s200 min-w-0 flex-1 overflow-hidden rounded-lg border">
        <div class="flex items-center gap-2 px-3 py-2">
          <span class="text-s1 text-lg font-medium">Modified</span>
          <span class="text-s500 text-lg"
            >{{ turn.fileDiffs.length }}
            {{ turn.fileDiffs.length === 1 ? 'file' : 'files' }}</span
          >
          <span class="text-s500 flex items-center gap-1 text-sm">
            <span class="text-green-600"
              >+{{ getTotalAdditions(turn.fileDiffs) }}</span
            >
            <span class="text-red-600"
              >-{{ getTotalDeletions(turn.fileDiffs) }}</span
            >
          </span>
        </div>
        <div
          *ngFor="let diff of turn.fileDiffs"
          class="border-s300 flex cursor-pointer items-center justify-between border-t px-3 py-2 hover:bg-gray-50"
          (click)="openFileDiff(diff)"
        >
          <span class="text-s500 min-w-0 truncate text-lg">{{
            diff.file
          }}</span>
          <span class="ml-3 flex flex-shrink-0 items-center gap-2 text-sm">
            <span *ngIf="diff.additions > 0" class="text-green-600"
              >+{{ diff.additions }}</span
            >
            <span *ngIf="diff.deletions > 0" class="text-red-600"
              >-{{ diff.deletions }}</span
            >
            <span
              *ngIf="diff.status"
              class="rounded px-1.5 py-0.5 text-lg font-medium"
              [ngClass]="{
                'text-green-700':
                  diff.status === 'added' || diff.status === 'modified',
                'text-red-700': diff.status === 'deleted'
              }"
              >{{ diff.status }}</span
            >
          </span>
        </div>
      </div>
    </div>

    <!-- Indicators only in the last turn -->
    <ng-container *ngIf="isLast">
      <div
        *ngIf="isActivating && !isSessionError"
        class="mb-4 ml-8 flex justify-start"
      >
        <div class="max-w-[90%] py-3">
          <span class="text-shimmer text-xl font-semibold leading-[1.5]"
            ><span
              *ngFor="let ch of activatingChars; trackBy: trackByIndex"
              class="text-shimmer-char"
              [style.--text-shimmer-index]="ch.index"
              >{{ ch.char }}</span
            ></span
          >
        </div>
      </div>
      <div *ngIf="isSessionError" class="mb-4 ml-10 flex justify-start">
        <div class="max-w-[90%] rounded-2xl bg-red-50 px-4 py-3">
          <div class="break-words text-xl text-red-700">
            Session activation failed. Please try again.
          </div>
        </div>
      </div>

      <!-- Session error indicator (compact, clickable) -->
      <div
        *ngIf="
          lastSessionError &&
          !isLastErrorRecovered &&
          !isSessionError &&
          !isAbortError()
        "
        class="mb-4 ml-[34px] flex items-start gap-3"
      >
        <span
          class="mt-4 h-2.5 w-2.5 flex-shrink-0 rounded-full bg-red-500"
        ></span>
        <button
          class="border-s200 max-w-[90%] overflow-hidden rounded-lg border text-left hover:bg-gray-50 focus:outline-none"
          (click)="openSessionErrorDialog()"
        >
          <div class="flex items-center gap-2 px-3 py-2">
            <span class="text-s1 text-lg font-medium">Error</span>
            <span class="text-s500 min-w-0 flex-1 truncate text-lg">{{
              getSessionErrorName()
            }}</span>
          </div>
        </button>
      </div>
    </ng-container>
  </div>
</ng-container>
